import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useApi } from "@/lib/api";
import { urgency } from "@/lib/crm";
import { ReminderRow } from "@/components/ReminderRow";
import { ReminderDialog } from "@/components/ReminderDialog";
import { CompleteReminderDialog } from "@/components/CompleteReminderDialog";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const key = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const DOT = { scaduto: "bg-rose-500", oggi: "bg-amber-400", futuro: "bg-sky-500" };

export default function CalendarPage() {
  const [month, setMonth] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; });
  const [sel, setSel] = useState(() => new Date());
  const [newRem, setNewRem] = useState(false);
  const [complete, setComplete] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const { data = [] } = useApi(`/reminders${showDone ? "" : "?done=false"}`);

  const byDay = useMemo(() => {
    const m = {};
    data.forEach((r) => { const k = key(new Date(r.due_at)); (m[k] = m[k] || []).push(r); });
    return m;
  }, [data]);

  const cells = useMemo(() => {
    const start = new Date(month);
    start.setDate(1 - ((month.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [month]);

  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const today = key(new Date());
  const selItems = byDay[key(sel)] || [];
  const defaultDate = new Date(sel); defaultDate.setHours(9, 0, 0, 0);

  return (
    <div className="space-y-6 fade-up" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Richiami e scadenze</h1>
          <p className="text-slate-500 mt-1">{data.filter((r) => !r.done).length} richiami aperti</p>
        </div>
        <div className="flex gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="calendar-show-done" type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />Mostra completati</label>
          <button data-testid="calendar-new-reminder-btn" onClick={() => setNewRem(true)} className="flex items-center gap-2 rounded-full bg-[#0F172A] text-white px-5 py-2.5 text-sm font-medium hover:bg-sky-600 transition-colors"><Plus className="h-4 w-4" />Nuovo richiamo</button>
        </div>
      </div>

      <div className="grid xl:grid-cols-[1.5fr_1fr] gap-6">
        <div className="bg-white border border-slate-200/80 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 data-testid="calendar-month-label" className="font-display font-bold text-xl capitalize">{month.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</h2>
            <div className="flex gap-1">
              <button data-testid="calendar-prev" onClick={() => shift(-1)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-slate-100"><ChevronLeft className="h-4 w-4" /></button>
              <button data-testid="calendar-today" onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); setSel(d); }} className="px-3 text-sm rounded-full hover:bg-slate-100">Oggi</button>
              <button data-testid="calendar-next" onClick={() => shift(1)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-slate-100"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 text-xs text-slate-400 uppercase tracking-wider mb-2">{DAYS.map((d) => <div key={d} className="px-2">{d}</div>)}</div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d) => {
              const k = key(d), items = byDay[k] || [], inMonth = d.getMonth() === month.getMonth(), isSel = k === key(sel);
              return (
                <button key={k} data-testid={`calendar-day-${d.getDate()}-${d.getMonth() + 1}`} onClick={() => setSel(d)}
                  className={`min-h-[78px] rounded-lg p-2 text-left border transition-colors ${isSel ? "border-sky-500 bg-sky-50" : "border-transparent hover:bg-slate-50"} ${inMonth ? "" : "opacity-40"}`}>
                  <span className={`text-sm ${k === today ? "inline-grid place-items-center h-6 w-6 rounded-full bg-[#0F172A] text-white" : "text-slate-700"}`}>{d.getDate()}</span>
                  <div className="mt-1 space-y-0.5">
                    {items.slice(0, 2).map((r) => (
                      <div key={r.reminder_id} className={`flex items-center gap-1 text-[11px] truncate ${r.done ? "line-through text-slate-400" : "text-slate-600"}`}>
                        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${r.done ? "bg-slate-300" : DOT[urgency(r.due_at)]}`} />{r.client_name}
                      </div>
                    ))}
                    {items.length > 2 && <p className="text-[11px] text-slate-400">+{items.length - 2}</p>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-slate-200/80 rounded-xl overflow-hidden self-start" data-testid="calendar-day-panel">
          <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
            <h3 className="font-display font-semibold capitalize">{sel.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</h3>
            <button data-testid="calendar-add-on-day" onClick={() => setNewRem(true)} className="text-sm text-sky-700 hover:underline">+ Aggiungi</button>
          </div>
          {selItems.length === 0 && <p className="text-sm text-slate-400 text-center py-10">Nessun richiamo in questa giornata.</p>}
          {selItems.map((r) => r.done
            ? <p key={r.reminder_id} className="px-5 py-3 text-sm text-slate-400 line-through border-b border-slate-100">{r.client_name} · {r.reason}</p>
            : <ReminderRow key={r.reminder_id} r={r} onComplete={setComplete} />)}
        </div>
      </div>
      <ReminderDialog open={newRem} onOpenChange={setNewRem} defaultDate={defaultDate} />
      <CompleteReminderDialog reminder={complete} onOpenChange={() => setComplete(null)} />
    </div>
  );
}
